import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FileDropzone } from '@/pages/dashboard/MyVehicles';
import { useForm } from 'react-hook-form';
import { trpc } from '@/trpc/client';
import { toast } from 'sonner';
import { Loader2, UploadCloud } from 'lucide-react';
import axios from 'axios';
import { useAuthStore } from '@/store/authStore';

const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';

interface CompanyVerificationModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess: () => void;
}

const CompanyVerificationModal: React.FC<CompanyVerificationModalProps> = ({ open, onOpenChange, onSuccess }) => {
    const { token } = useAuthStore();
    const [isUploading, setIsUploading] = useState(false);
    const utils = trpc.useUtils();

    const mutation = trpc.user.verifyCompany.useMutation({
        onSuccess: () => {
            toast.success('Company verification submitted successfully!');
            utils.user.getProfile.invalidate(); // Refresh user status
            onSuccess();
            onOpenChange(false);
        },
        onError: (err) => toast.error(err.message)
    });

    const [file, setFile] = useState<File | null>(null);

    const handleUploadAndSubmit = async () => {
        if (!file) {
            toast.error('Please select a document first.');
            return;
        }

        try {
            setIsUploading(true);
            // 1. Upload File
            const fd = new FormData();
            fd.append('files', file);
            fd.append('type', 'company_documents');

            const { data } = await axios.post(`${apiUrl}/upload`, fd, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            const filePath = data.files[0].filePath;

            // 2. Submit Verification
            await mutation.mutateAsync({ documentPath: filePath });

        } catch (error) {
            console.error(error);
            toast.error('Upload failed. Please try again.');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md w-full rounded-xl p-6">
                <DialogHeader>
                    <DialogTitle>Company Verification</DialogTitle>
                    <DialogDescription className="text-sm text-balance">
                        Please upload your <strong>Business Registration</strong> to verify your company and start managing your fleet.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 transition-colors flex flex-col items-center justify-center space-y-3 relative bg-muted/5">
                        <input
                            type="file"
                            accept=".pdf,.png,.jpg,.jpeg"
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-50"
                            onChange={(e) => {
                                const files = e.target.files;
                                if (files && files.length > 0) setFile(files[0]);
                            }}
                        />
                        <div className="p-3 bg-background rounded-full shadow-sm">
                            <UploadCloud className="w-6 h-6 text-primary" />
                        </div>
                        <div className="text-sm">
                            {file ? (
                                <span className="font-semibold text-primary break-all line-clamp-1 px-4">{file.name}</span>
                            ) : (
                                <span className="text-muted-foreground px-4">Tap to upload Registration Document</span>
                            )}
                        </div>
                    </div>

                    <Button
                        onClick={handleUploadAndSubmit}
                        className="w-full h-12 text-base font-medium"
                        disabled={!file || isUploading || mutation.isLoading}
                    >
                        {(isUploading || mutation.isLoading) && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
                        Submit Document
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default CompanyVerificationModal;
